import {
  Accordion,
  Accordions,
  AccountAddressDetails,
  AccountDetails,
  AccountOrderList,
  AccountShopList,
  BlogPostCard,
  CollectionCarousel,
  ContentCarousel,
  CreateShopForm,
  EditShopForm,
  Freeform,
  Iframe,
  LoginForm,
  Newsletter,
  PasswordRecoveryForm,
  ProductBundleForm,
  ProductCard,
  SignupForm,
  Video,
} from '@data/sanity/schema'
import { SanityBlogPostWithoutBody, SanityBlogPostOptions } from './blog'
import { SanityComplexPortableText, SanitySimplePortableText } from './content'
import { SanityImageFragment } from './image'
import { SanityProductBundle, SanityProductFragment } from './product'
import {
  SanityAccountAddressDetailsStrings,
  SanityAccountDetailsStrings,
  SanityAccountOrderListStrings,
  SanityAccountShopListStrings,
  SanityAuthStrings,
  SanityShopFormStrings,
} from './strings'
import { SanityVideo } from './video'

export enum SanityBlockType {
  ACCORDIONS = 'accordions',
  ACCOUNT_ADDRESS_DETAILS = 'accountAddressDetails',
  ACCOUNT_DETAILS = 'accountDetails',
  ACCOUNT_ORDER_LIST = 'accountOrderList',
  ACCOUNT_SHOP_LIST = 'accountTeamShopList',
  BLOG_POST_CARD = 'blogPostCard',
  COLLECTION_CAROUSEL = 'collectionCarousel',
  CONTENT_CAROUSEL = 'contentCarousel',
  CREATE_SHOP_FORM = 'createShopForm',
  EDIT_SHOP_FORM = 'editShopForm',
  FREEFORM = 'freeform',
  IFRAME = 'iframe',
  LOGIN_FORM = 'loginForm',
  NEWSLETTER = 'newsletter',
  PASSWORD_RECOVERY_FORM = 'passwordRecoveryForm',
  PRODUCT_BUNDLE_FORM = 'productBundleForm',
  PRODUCT_CARD = 'productCard',
  SIGNUP_FORM = 'signupForm',
  VIDEO = 'video',
}

export type SanityFreeformBlock = Pick<
  Freeform,
  '_type' | 'textAlign' | 'maxWidth'
> & {
  _key: string
  content: SanityComplexPortableText
}

export type SanityFreeform = Omit<SanityFreeformBlock, '_type' | '_key'>

export type SanityAccordionsBlockItem = Pick<Accordion, 'title'> & {
  id: string
  content: SanitySimplePortableText
}

export type SanityAccordionsBlock = Pick<Accordions, '_type'> & {
  _key: string
  items: SanityAccordionsBlockItem[]
}

export type SanityAccordions = Omit<SanityAccordionsBlock, '_type' | '_key'>

export type SanityProductCardBlock = Pick<ProductCard, '_type'> & {
  _key: string
  product: SanityProductFragment
}

export type SanityProductCard = Omit<SanityProductCardBlock, '_type' | '_key'>

export type SanitySignupFormBlock = SignupForm & {
  _key: string
  authStrings: SanityAuthStrings
}

export type SanityLoginFormBlock = LoginForm & {
  _key: string
  authStrings: SanityAuthStrings
}

export type SanityPasswordRecoveryFormBlock = PasswordRecoveryForm & {
  _key: string
  authStrings: SanityAuthStrings
}

export type SanityAccountDetailsBlock = AccountDetails & {
  _key: string
  accountStrings: SanityAccountDetailsStrings
}

export type SanityAccountAddressDetailsBlock = AccountAddressDetails & {
  _key: string
  accountStrings: SanityAccountAddressDetailsStrings
}

export type SanityAccountOrderListBlock = AccountOrderList & {
  _key: string
  accountStrings: SanityAccountOrderListStrings
}

export type SanityAccountShopListBlock = Pick<
  AccountShopList,
  '_type' | 'active'
> & {
  _key: string
  accountStrings: SanityAccountShopListStrings
}

export type SanityVideoBlock = Pick<Video, '_type'> &
  SanityVideo & {
    _key: string
  }

export type SanityCollectionCarouselBlock = Pick<
  CollectionCarousel,
  '_type'
> & {
  _key: string
  collection: {
    products: SanityProductFragment[]
  }
}

export type SanityCollectionCarousel = Omit<
  SanityCollectionCarouselBlock,
  '_type' | '_key'
>

export type SanityNewsletterBlock = Pick<
  Newsletter,
  '_type' | 'klaviyoListID' | 'submit'
> & {
  _key: string
  successMsg: SanityComplexPortableText
  errorMsg: SanityComplexPortableText
  terms?: SanitySimplePortableText
}

export type SanityNewsletter = Omit<SanityNewsletterBlock, '_type' | '_key'>

export type SanityProductBundleFormBlock = Pick<
  ProductBundleForm,
  '_type' | 'showGallery'
> & {
  _key: string
  productBundle: SanityProductBundle
}

export type SanityBlogPostCardBlock = Pick<BlogPostCard, '_type'> & {
  _key: string
  post: SanityBlogPostWithoutBody
  options?: SanityBlogPostOptions
}

export enum SanityContentCarouselItemType {
  FREEFORM = 'freeform',
  PHOTO = 'photo',
  PRODUCT = 'product',
  BLOG_POST = 'blogPost',
  VIDEO = 'video',
}

interface SanityContentCarouselFreeformItem {
  _key: string
  _type: SanityContentCarouselItemType.FREEFORM
  freeform: SanityFreeformBlock
}

interface SanityContentCarouselPhotoItem {
  _key: string
  _type: SanityContentCarouselItemType.PHOTO
  photo: SanityImageFragment
}

interface SanityContentCarouselProductItem {
  _key: string
  _type: SanityContentCarouselItemType.PRODUCT
  product: SanityProductFragment
}

interface SanityContentCarouselBlogPostItem {
  _key: string
  _type: SanityContentCarouselItemType.BLOG_POST
  blogPost: SanityBlogPostWithoutBody
}

interface SanityContentCarouselVideoItem {
  _key: string
  _type: SanityContentCarouselItemType.VIDEO
  video: SanityVideo
}

export type SanityContentCarouselItem =
  | SanityContentCarouselFreeformItem
  | SanityContentCarouselPhotoItem
  | SanityContentCarouselProductItem
  | SanityContentCarouselBlogPostItem
  | SanityContentCarouselVideoItem

export interface SanityContentCarousel {
  items: SanityContentCarouselItem[]
}

export type SanityContentCarouselBlock = Pick<ContentCarousel, '_type'> &
  SanityContentCarousel & {
    _key: string
  }

export type SanityCreateShopForm = Pick<CreateShopForm, 'active'> & {
  authStrings: SanityAuthStrings
  shopFormStrings: SanityShopFormStrings
}

export type SanityCreateShopFormBlock = Pick<CreateShopForm, '_type'> &
  SanityCreateShopForm & {
    _key: string
  }

export type SanityEditShopForm = Pick<EditShopForm, 'active'> & {
  authStrings: SanityAuthStrings
  shopFormStrings: SanityShopFormStrings
}

export type SanityEditShopFormBlock = Pick<EditShopForm, '_type'> &
  SanityEditShopForm & {
    _key: string
  }

export type SanityIframeBlock = Pick<
  Iframe,
  '_type' | 'title' | 'url' | 'width' | 'height'
> & {
  _key: string
}

export type SanityBlocks =
  | SanityFreeformBlock
  | SanityAccordionsBlock
  | SanityProductCardBlock
  | SanitySignupFormBlock
  | SanityLoginFormBlock
  | SanityPasswordRecoveryFormBlock
  | SanityAccountDetailsBlock
  | SanityAccountAddressDetailsBlock
  | SanityAccountOrderListBlock
  | SanityAccountShopListBlock
  | SanityVideoBlock
  | SanityCollectionCarouselBlock
  | SanityNewsletterBlock
  | SanityProductBundleFormBlock
  | SanityBlogPostCardBlock
  | SanityContentCarouselBlock
  | SanityCreateShopFormBlock
  | SanityEditShopFormBlock
  | SanityIframeBlock
